import { useCallback, useContext, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { normaliseOccupancies, normaliseRoomOccupancies } from 'businessTraveller/utils/normaliseOccupancies'
import setSelectedBusinessTravellersAndGuests from 'actions/businessTraveller/setSelectedBusinessTravellerEmployeesAndGuests'
import { saveBusinessTravellerSelectedTravellerState } from 'businessTraveller/storage/businessTravellerStorage'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { selectBusinessTravellerCurrentApprovalRequest } from 'businessTraveller/selectors/selectBusinessTravellerCurrentApprovalRequest'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'

interface UseMultiRoomSelectionBaseParams<T extends App.Occupants> {
  occupancies: Array<T>
  setOccupancies: (occupancies: Array<T>) => void
  normalise: (travellerCount: number, roomCount: number) => Array<T>
}

export type UseMultiRoomSelectionReturn = ReturnType<typeof useMultiRoomSelectionSearchBar>

function useMultiRoomSelectionBase<T extends App.Occupants>({ occupancies, setOccupancies, normalise }: UseMultiRoomSelectionBaseParams<T>) {
  const appDispatch = useAppDispatch()

  const travellerEmployees = useAppSelector(selectSelectedTravellerEmployees)
  const guestCount = useAppSelector(state => state.businessTraveller.selectedTravellerGuestCount)
  const approvalRequest = useAppSelector(selectBusinessTravellerCurrentApprovalRequest)

  const totalTravellers = travellerEmployees.length + guestCount
  const roomCount = totalTravellers === 0 ? 0 : occupancies.length

  const setOccupants = useCallback((employeeIds: Array<string>, guestCount: number, roomCount?: number) => {
    const travellerCount = employeeIds.length + guestCount
    appDispatch(setSelectedBusinessTravellersAndGuests(employeeIds, guestCount))
    const newOccupancies = normalise(travellerCount, roomCount ?? Math.min(occupancies.length, travellerCount))
    setOccupancies(newOccupancies)
    // Save to localstorage so persist on page refresh
    saveBusinessTravellerSelectedTravellerState(employeeIds, guestCount)
  }, [appDispatch, occupancies.length, setOccupancies, normalise])

  const onAddEmployee = useCallback((employeeId: string) => {
    const selectedEmployeeIds = [...travellerEmployees.map(employee => employee.id), employeeId]
    const newOccupantCount = selectedEmployeeIds.length + guestCount

    if (approvalRequest) {
      showSnackbar(
        'If you need to change the travellers, please create a new approval request.',
        'warning',
      )
      return
    }
    // Add new room when adding employee
    if (newOccupantCount > 1) {
      setOccupants(selectedEmployeeIds, guestCount, occupancies.length + 1)
    } else {
      setOccupants(selectedEmployeeIds, guestCount)
    }
  }, [guestCount, travellerEmployees, setOccupants, approvalRequest, occupancies.length])

  const onRemoveEmployee = useCallback((employeeId: string) => {
    const selectedEmployeeIds = travellerEmployees.filter(employee => employee.id !== employeeId).map(employee => employee.id)
    const newOccupantCount = selectedEmployeeIds.length + guestCount
    // Remove a room if there will be any empty rooms
    if (newOccupantCount < occupancies.length) {
      setOccupants(selectedEmployeeIds, guestCount, (occupancies.length - 1) || 1)
    } else {
      setOccupants(selectedEmployeeIds, guestCount)
    }
  }, [travellerEmployees, guestCount, occupancies.length, setOccupants])

  const onRemoveGuest = useCallback(() => {
    const selectedEmployeeIds = travellerEmployees.map(employee => employee.id)
    const newOccupantCount = selectedEmployeeIds.length + guestCount - 1
    // Remove a room if there will be any empty rooms
    if (newOccupantCount < occupancies.length) {
      setOccupants(selectedEmployeeIds, guestCount - 1, (occupancies.length - 1) || 1)
    } else {
      setOccupants(selectedEmployeeIds, guestCount - 1)
    }
  }, [travellerEmployees, guestCount, occupancies.length, setOccupants])

  const onAddGuest = useCallback(() => {
    const newOccupantCount = travellerEmployees.length + guestCount + 1

    if (approvalRequest) {
      showSnackbar(
        'If you need to change the travellers, please create a new approval request.',
        'warning',
      )
      return
    }
    // Add new room when adding guest
    if (newOccupantCount > 1) {
      setOccupants(travellerEmployees.map(employee => employee.id), guestCount + 1, occupancies.length + 1)
    } else {
      setOccupants(travellerEmployees.map(employee => employee.id), guestCount + 1)
    }
  }, [travellerEmployees, guestCount, setOccupants, approvalRequest, occupancies.length])

  const onRoomCountChange = useCallback((roomCount: number) => {
    const newOccupancies = normalise(totalTravellers, roomCount)
    setOccupancies(newOccupancies)
  }, [setOccupancies, totalTravellers, normalise])

  return {
    // Traveller state
    travellerEmployees,
    guestCount,
    totalTravellers,
    // Room state
    occupancies,
    roomCount,
    // Traveller actions
    setOccupants,
    onAddEmployee,
    onRemoveEmployee,
    onAddGuest,
    onRemoveGuest,
    // Room actions
    onRoomCountChange,
  }
}

/**
 * Hook for multi-room selection in the search bar.
 * Uses GlobalSearchStateContext for occupancies.
 */
export function useMultiRoomSelectionSearchBar() {
  const globalSearchState = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const occupancies = useMemo(() => globalSearchState.occupancies ?? [], [globalSearchState.occupancies])

  const setOccupancies = useCallback((occupancies: Array<App.Occupants>) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies })
  }, [globalSearchDispatch])

  return useMultiRoomSelectionBase({ occupancies, setOccupancies, normalise: normaliseOccupancies })
}

/**
 * Hook for multi-room selection in the offer page.
 * Uses OfferPageContext for rooms instead of GlobalSearchStateContext.
 */
export function useMultiRoomSelectionOfferPage() {
  const offerPageState = useContext(OfferPageStateContext)
  const offerPageDispatch = useOfferPageDispatch()

  const occupancies = useMemo(() => offerPageState.rooms ?? [], [offerPageState.rooms])

  const setOccupancies = useCallback((occupancies: Array<App.RoomOccupants>) => {
    offerPageDispatch({ type: OfferPageActions.setRooms, rooms: occupancies })
  }, [offerPageDispatch])

  return useMultiRoomSelectionBase({ occupancies, setOccupancies, normalise: normaliseRoomOccupancies })
}
